import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { QueryClient, useQuery } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { RouterProvider } from "@tanstack/react-router";
import { del, get, set } from "idb-keyval";
import { StrictMode, useEffect, useState } from "react";
import { createRoot } from "react-dom/client";
import { api } from "./lib/api.ts";
import { keys } from "./lib/queries.ts";
import { Login, Setup } from "./pages/Login.tsx";
import { router } from "./router.tsx";
import "./styles.css";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      gcTime: 7 * 86_400_000,
      refetchOnWindowFocus: true,
      retry: (n, e) => n < 2 && (e as { status?: number }).status !== 401 && (e as { status?: number }).status !== 404,
    },
  },
});

// The last data we had is shown instantly on the next visit, then refreshed.
const persister = createAsyncStoragePersister({
  storage: {
    getItem: (k) => get<string>(k).catch(() => null),
    setItem: (k, v) => set(k, v).catch(() => {}),
    removeItem: (k) => del(k).catch(() => {}),
  },
  key: "hammer-cache-v1",
  throttleTime: 2000,
});

function Gate() {
  const session = useQuery({ queryKey: keys.session, queryFn: () => api<{ authRequired: boolean; authed: boolean; setupRequired?: boolean }>("/session"), staleTime: 60_000, retry: 1 });
  const [loggedOut, setLoggedOut] = useState(false);
  useEffect(() => {
    const f = () => setLoggedOut(true);
    window.addEventListener("hammer:logged-out", f);
    return () => window.removeEventListener("hammer:logged-out", f);
  }, []);
  if (session.isPending && !session.data) return null;
  if (session.data?.setupRequired) return <Setup />;
  const needLogin = loggedOut || (session.data?.authRequired && !session.data.authed);
  if (needLogin && location.pathname !== "/capture")
    return <Login onDone={() => { setLoggedOut(false); void queryClient.invalidateQueries(); void session.refetch(); }} />;
  return <RouterProvider router={router} />;
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <PersistQueryClientProvider client={queryClient} persistOptions={{ persister, maxAge: 7 * 86_400_000, buster: "1" }}>
      <Gate />
    </PersistQueryClientProvider>
  </StrictMode>,
);

if ("serviceWorker" in navigator && import.meta.env.PROD) {
  navigator.serviceWorker.register("/sw.js").catch(() => {});
}
